/**
 * L'ATTENDU ET LE VERDICT, FLUX PAR FLUX (DEV-1845 tranche 2, DEV-1508).
 * ============================================================================
 *
 * INTENTION : un client qui regarde son compteur lit ce qui lui est promis sur
 * CHAQUE flux qu'il attend, et où en est chacun. « Courbe 30 min · dernière
 * mesure le 16/09/2026 · attendu J+1 », et juste en dessous « Énergie
 * journalière · dernière mesure le 08/09/2026 · attendu J+1 · en retard de 9 j ».
 * Un seul chiffre agrégé ne pouvait pas dire ça : il écrivait « attendu J+3 »
 * partout, ce qui était le SEUIL D'ALERTE de la collecte et non la promesse.
 *
 * LES DEUX CHIFFRES NE SE CONFONDENT PLUS :
 *  • `attendu_jours` = la PROMESSE. C'est le seul qui s'écrit « attendu J+n » ;
 *  • `alerte_jours`  = le seuil qui a tranché `a_jour`. Il ne s'écrit JAMAIS.
 *
 * ET RIEN N'EST FABRIQUÉ ICI : aucun seuil, aucun attendu par défaut, aucune
 * date déduite. Un flux sans promesse chiffrée ne reçoit pas un « J+n » de
 * consolation : il reçoit le MOT qui dit de quelle nature il est (« stock »,
 * « témoin de communication, jamais un verdict »), et rien quand même ce mot
 * n'est pas connu.
 *
 * ENFIN, `flux` EST ABSENTE TANT QUE DAPI NE LA PORTE PAS. Toutes les fonctions
 * de ce module rendent alors `null`, et la page garde exactement le comportement
 * qu'elle avait (règles des lots 5h et 5g annexe c).
 */
import type { StatusCollectePdl, StatusFluxPdl } from "@types"
import type { DirectionEnedis } from "@modules/data/services/sourceDataEnedis.service"
import type { CleOnglet } from "@modules/data/utils/ongletsEnedis"
import { dateFr } from "@modules/data/utils/fraicheurEnedis"
import { jourDeLecture } from "@modules/data/utils/pageCompteurV2"

/**
 * L'ÉTAT D'UNE LIGNE, et la couleur de sa pastille. Quatre, pas trois :
 *  • `a-jour`   vert   — la promesse est tenue ;
 *  • `retard`   ambre  — elle ne l'est pas, et le retard est chiffré ;
 *  • `collecte` violet — c'est à NOUS d'agir, pas au distributeur ;
 *  • `attente`  gris   — rien n'est jamais arrivé, ou le verdict n'est pas su.
 *    Le gris est NEUTRE : il n'accuse personne et n'annonce rien en chemin.
 */
export type EtatDeLigne = "a-jour" | "retard" | "collecte" | "attente"

/** Une ligne de flux à peindre : son texte, son état, sa clé. */
export type LigneDeFlux = {
    cle: string
    texte: string
    etat: EtatDeLigne
}

/**
 * CE QUE LA PAGE SAIT DU COMPTEUR et dont les libellés ont besoin. Rien n'y est
 * obligatoire : un fait absent fait tomber le libellé sur son mot générique,
 * jamais sur une valeur inventée.
 */
export type ContexteDesFlux = {
    /** Le segment servi (`C5`, `C4`, `C2`…), qui décide du grain de la PMax. */
    segment?: string | null
    /** Le pas de la courbe, PAR SENS : « 5 min », « 30 min »… */
    pasDeLaCourbe?: Partial<Record<DirectionEnedis, string | null>> | null
    /** Vrai quand le compteur porte les deux sens : le sens se nomme alors. */
    deuxSens?: boolean
}

// ============================================================================
// LE NOM DU FLUX, DIT AU CLIENT
// ============================================================================

/** Les segments dont la PMax est relevée au JOUR. Ailleurs, elle suit le mois. */
const SEGMENTS_PMAX_QUOTIDIENNE = ["C5", "P4"]

/** Les seuls sens que la page sache nommer. Tout le reste ne s'écrit pas. */
const SENS_NOMMABLES = ["soutirage", "injection"]

/**
 * LE NOM D'UN FLUX À L'ÉCRAN, ou `null` quand on ne sait pas le nommer.
 *
 * `null` n'est pas un oubli : le vocabulaire amont est OUVERT, et écrire la clé
 * servie telle quelle (`energie_horaire`, `m023`) mettrait notre plomberie sur
 * l'écran d'un client. Un flux qu'on ne sait pas nommer ne reçoit pas de ligne,
 * mais il reste COMPTÉ dans le résumé : le nombre de flux dus reste juste.
 */
export function libelleDuFlux(
    flux: StatusFluxPdl | null | undefined,
    contexte: ContexteDesFlux = {}
): string | null {
    // UN SENS INATTENDU NE SE NOMME PAS (revue du 17/09). Le vocabulaire des
    // sens est OUVERT comme celui des flux : un `sens` ni `soutirage` ni
    // `injection` — inédit, ou simplement non servi — tombait ici sur le pas de
    // courbe du soutirage et s'écrivait sans qualificatif, donc SE LISAIT comme
    // du soutirage. Une étiquette fausse est pire qu'une ligne absente : ce flux
    // est traité comme un flux qu'on ne sait pas nommer, sans ligne mais COMPTÉ
    // dans le résumé, et sa valeur brute n'atteint jamais l'écran.
    if (!SENS_NOMMABLES.includes(String(flux?.sens ?? ""))) return null
    const base = nomDeBase(flux, contexte)
    if (!base) return null
    // LE SENS NE SE NOMME QUE QUAND IL Y A DE QUOI SE TROMPER (maquette v1.5) :
    // sur un compteur à un seul sens, « (injection) » n'apprend rien et alourdit
    // chaque ligne. Le soutirage, lui, ne se nomme jamais : c'est le défaut.
    if (contexte.deuxSens && flux?.sens === "injection") {
        return `${base} (injection)`
    }
    return base
}

function nomDeBase(
    flux: StatusFluxPdl | null | undefined,
    contexte: ContexteDesFlux
): string | null {
    switch (flux?.flux) {
        case "cdc": {
            // Le pas vient du compteur (`data.<sens>.cdc.pas`), jamais d'un
            // défaut : sans lui, la courbe se nomme sans son grain.
            // Le sens est déjà garanti nommable par l'appelant : ce n'est plus
            // un défaut, c'est le sens SERVI.
            const sens = flux?.sens === "injection" ? "injection" : "soutirage"
            const pas = contexte.pasDeLaCourbe?.[sens]
            return pas ? `Courbe ${pas}` : "Courbe de charge"
        }
        case "energie_horaire":
            return "Énergie horaire"
        case "energie":
            return "Énergie journalière"
        case "pmax":
            return SEGMENTS_PMAX_QUOTIDIENNE.includes(
                String(contexte.segment ?? "")
            )
                ? "PMax quotidienne"
                : "PMax mensuelle"
        case "index":
            return "Index"
        default:
            return null
    }
}

// ============================================================================
// CE QUI EST PROMIS SUR CE FLUX
// ============================================================================

/**
 * LE MOT D'UN FLUX SANS PROMESSE CHIFFRÉE, par la clé de la ligne d'attendu qui
 * a tranché. La clé est du vocabulaire interne : elle ne s'écrit jamais, elle
 * CHOISIT le mot client.
 *
 * Une clé absente de cette table ne reçoit pas de mot : « defaut » veut dire
 * qu'aucune promesse n'a été jugée pour ce couple, et broder dessus reviendrait
 * à annoncer un attendu que personne n'a tenu.
 */
export const MOT_DE_L_ATTENDU: Record<string, string> = {
    temoin_index: "témoin de communication, jamais un verdict",
    agregees_hors_fraicheur: "stock",
    m023_pmax_c5: "sur commande, délai variable",
    // Pas encore servie : la relève cyclique n'est pas branchée sur l'attendu
    // amont (trou mesuré et nommé côté enedis). Le mot est prêt pour le jour où
    // elle le sera ; d'ici là, cette entrée ne se déclenche jamais.
    releve_cyclique: "au cycle de relève",
}

/**
 * CE QUI EST ATTENDU DE CE FLUX, en mots de client, ou `null` quand rien n'est
 * promis ni nommé.
 *
 * LA PROMESSE PRIME et ne vient QUE de `attendu_jours`. `alerte_jours` n'entre
 * jamais ici : c'est le seuil sur lequel `a_jour` a été tranché, et l'écrire
 * « attendu J+3 » à un client à qui J+1 est promis est exactement le défaut que
 * cette tranche corrige.
 */
export function attenduDuFlux(
    flux: StatusFluxPdl | null | undefined
): string | null {
    if (typeof flux?.attendu_jours === "number") {
        return `attendu J+${flux.attendu_jours}`
    }
    const cle = flux?.attendu
    return (cle && MOT_DE_L_ATTENDU[cle]) || null
}

// ============================================================================
// L'ÉTAT D'UN FLUX, ET SA PHRASE
// ============================================================================

/** Un jour ISO avancé de `n` jours, ou `null` sur ce qui n'est pas un jour. */
function jourDecale(jour: string, n: number): string | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) return null
    const d = new Date(`${jour}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return null
    d.setUTCDate(d.getUTCDate() + n)
    return d.toISOString().slice(0, 10)
}

/**
 * LE DERNIER JOUR DONT LA DONNÉE DE CE FLUX EST DUE (lot 6a, volet 2).
 *
 * CONSIGNE DE MATHIEU, mot pour mot (21/09/2026) : « Il faut que la page sache
 * sous quel délai on attend les données pour savoir si c'est là ou pas. » Ce
 * jour-là est la réponse : au-delà, la donnée n'est pas absente, elle n'est
 * PAS ENCORE DUE.
 *
 * LA PROMESSE, ET ELLE SEULE (`attendu_jours`). `alerte_jours` n'entre pas ici :
 * c'est le seuil sur lequel `a_jour` a été tranché, et l'employer repousserait
 * la limite du dû de deux jours sur un parc à qui J+1 est promis — exactement
 * la confusion que la tranche 2 a corrigée dans les phrases.
 *
 * `null` quand rien n'est promis (`attendu_jours: null` : relève cyclique, PMax
 * C5) ou quand le jour de lecture n'est pas un jour : aucun jugement de
 * présence n'est alors fabriqué, et la page garde le comportement d'avant.
 */
export function dernierJourDuDuFlux(
    flux: StatusFluxPdl | null | undefined,
    aujourdHui: string
): string | null {
    const promis = flux?.attendu_jours
    if (typeof promis !== "number" || !Number.isFinite(promis)) return null
    return jourDecale(String(aujourdHui).slice(0, 10), -Math.trunc(promis))
}

/**
 * LE FLUX SE TIENT-IL DEBOUT TOUT SEUL (même règle qu'au lot 5h, portée sur la
 * bonne colonne).
 *
 * Un statut est figé au dernier passage de la collecte : son « à jour » date de
 * ce passage-là. Si la dernière mesure servie, augmentée du SEUIL sur lequel ce
 * verdict a été tranché (`alerte_jours`, pas la promesse), tombe avant la
 * veille, le verdict ne peut plus être vrai aujourd'hui.
 *
 * On ne conclut rien de plus : ni un retard, ni une cause. Et un fait manquant
 * ne rend pas le verdict incroyable — sans date ni seuil servis, il n'y a rien
 * à confronter.
 */
export function verdictDuFluxCredible(
    flux: StatusFluxPdl | null | undefined,
    aujourdHui: string
): boolean {
    if (flux?.a_jour !== true) return true
    const mesure = String(flux?.derniere_mesure ?? "").slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(mesure)) return true
    if (typeof flux?.alerte_jours !== "number") return true
    const limite = jourDecale(mesure, flux.alerte_jours)
    const veille = jourDecale(String(aujourdHui).slice(0, 10), -1)
    if (!limite || !veille) return true
    return limite >= veille
}

/**
 * LA COULEUR DE LA PASTILLE D'UN FLUX.
 *
 * Le violet ne se déduit pas du flux : il vient du responsable NOMMÉ par le
 * statut agrégé (`a_qui_dagir`, `cause`). Sans ce fait, un retard reste ambre —
 * on ne s'accuse pas au hasard, et on n'accuse pas le distributeur non plus.
 */
export function etatDuFlux(
    flux: StatusFluxPdl | null | undefined,
    statut: StatusCollectePdl | null | undefined,
    aujourdHui: string
): EtatDeLigne {
    if (flux?.derniere_mesure === null) return "attente"
    if (!verdictDuFluxCredible(flux, aujourdHui)) return "attente"
    if (flux?.a_jour === true) return "a-jour"
    if (flux?.a_jour !== false) return "attente"
    const nous =
        statut?.a_qui_dagir === "homeys" || statut?.cause === "retard_homeys"
    return nous ? "collecte" : "retard"
}

/**
 * LA PHRASE D'UN FLUX, ou `null` quand il n'est pas nommable.
 *
 * Quatre morceaux au plus, chacun conditionné à son fait : le nom, la date (ou
 * l'aveu qu'il n'y en a jamais eu), l'attendu, et l'ÉTAT — « à jour », ou le
 * retard quand le verdict le dit en retard.
 *
 * L'ÉTAT S'ÉCRIT DEPUIS LA REVIEW DU 23/09 (S1, mots de Mathieu : « son état
 * (pastille + à jour / en retard de n j) »). Avant, « à jour » ne figurait que
 * sur la pastille, au motif que le répéter noierait les retards ; une pastille
 * verte seule ne se lit pas au clavier, ne survit pas à une copie d'écran en
 * noir et blanc, et laissait deviner le verdict de chaque ligne.
 *
 * `attente` ne reçoit RIEN : un flux dont le verdict n'est pas su ne se dit ni
 * à jour ni en retard, et sa date — ou son « aucune mesure reçue à ce jour » —
 * porte déjà tout ce qui est établi.
 */
export function texteDuFlux(
    flux: StatusFluxPdl | null | undefined,
    contexte: ContexteDesFlux,
    etat: EtatDeLigne
): string | null {
    const nom = libelleDuFlux(flux, contexte)
    if (!nom) return null
    const morceaux: string[] = [nom]
    const date = dateFr(flux?.derniere_mesure)
    if (date) morceaux.push(`dernière mesure le ${date}`)
    else if (flux?.derniere_mesure === null)
        morceaux.push("aucune mesure reçue à ce jour")
    const attendu = attenduDuFlux(flux)
    if (attendu) morceaux.push(attendu)
    if (etat === "a-jour") {
        morceaux.push("à jour")
    } else if (
        (etat === "retard" || etat === "collecte") &&
        typeof flux?.retard_jours === "number"
    ) {
        morceaux.push(`en retard de ${flux.retard_jours} j`)
    } else if (etat === "retard" || etat === "collecte") {
        morceaux.push("en retard")
    }
    return morceaux.join(" · ")
}

/** Une clé stable par ligne, même si deux flux portent le même nom. */
function cleDuFlux(flux: StatusFluxPdl | null | undefined, i: number): string {
    const sens = flux?.sens ? String(flux.sens) : "?"
    const nom = flux?.flux ? String(flux.flux) : `flux-${i}`
    return `${sens}:${nom}:${i}`
}

// ============================================================================
// LE BLOC DE FRAÎCHEUR, UNE LIGNE PAR FLUX DÛ
// ============================================================================

export type FraicheurParFlux = {
    /**
     * UNE LIGNE PAR FLUX DÛ NOMMABLE, dans l'ordre servi. Aucune n'est repliée
     * ni résumée : c'est la demande de Mathieu du 23/09 (S1).
     */
    lignes: LigneDeFlux[]
    /** « 3 flux dus · 2 à jour · 1 en retard de 9 j ». */
    resume: string
    /** `true`/`false` quand les verdicts le disent, `null` quand ils se taisent. */
    tousAJour: boolean | null
}

/**
 * LA FRAÎCHEUR, FLUX PAR FLUX, ou `null` quand il n'y a rien à en dire.
 *
 * `null` couvre les deux cas où la page doit garder son comportement d'avant :
 * `flux` absente ou non servie (dapi pas déployé, juge désarmé), et `flux: []`
 * (servie, et ce compteur ne doit aucun flux récurrent). Dans les deux cas, la
 * voie courte du lot 5a reprend la main, seule.
 */
export function fraicheurParFlux(
    statut: StatusCollectePdl | null | undefined,
    contexte: ContexteDesFlux = {},
    /** Défaut : le jour civil de Paris, jamais le jour UTC (lot 6b). */
    aujourdHui: string = jourDeLecture()
): FraicheurParFlux | null {
    const servis = statut?.flux
    if (!Array.isArray(servis) || servis.length === 0) return null

    const etats = servis.map((flux) => etatDuFlux(flux, statut, aujourdHui))
    const toutes: LigneDeFlux[] = []
    servis.forEach((flux, i) => {
        const texte = texteDuFlux(flux, contexte, etats[i])
        // UN FLUX QU'ON NE SAIT PAS NOMMER N'A PAS DE LIGNE, mais il reste
        // compté dans le résumé : le nombre de flux dus reste juste.
        if (texte)
            toutes.push({ cle: cleDuFlux(flux, i), texte, etat: etats[i] })
    })

    // PLUS AUCUN REPLI (review de Mathieu, 23/09) : « plus jamais n autres
    // flux ». Le repli gagnait deux lignes d'écran et coûtait le nom, la date
    // et l'attendu des flux qu'il avalait — sur un C5 à quatre flux, trois des
    // quatre promesses n'étaient plus lisibles qu'au survol, donc jamais au
    // clavier ni sur une copie d'écran. L'ordre reste celui servi par dapi :
    // retrier par état déplacerait une ligne sous les yeux du client d'un
    // chargement à l'autre.
    return {
        lignes: toutes,
        resume: resumeDesFlux(servis, etats),
        tousAJour: verdictDesFlux(etats),
    }
}

/**
 * LE RÉSUMÉ, compté sur TOUS les flux dus, y compris ceux qu'on ne sait pas
 * nommer : « 3 flux dus · 2 à jour · 1 en retard de 9 j ». Chaque morceau n'est
 * écrit que s'il compte au moins un flux, et le retard n'est chiffré que si au
 * moins un flux en retard porte son nombre de jours.
 */
export function resumeDesFlux(
    servis: StatusFluxPdl[],
    etats: EtatDeLigne[]
): string {
    const n = servis.length
    const morceaux = [`${n} flux ${n > 1 ? "dus" : "dû"}`]
    const compte = (etat: EtatDeLigne) => etats.filter((e) => e === etat).length
    const aJour = compte("a-jour")
    if (aJour > 0) morceaux.push(`${aJour} à jour`)

    const enRetard = servis.filter(
        (_, i) => etats[i] === "retard" || etats[i] === "collecte"
    )
    if (enRetard.length > 0) {
        const jours = enRetard
            .map((f) => f.retard_jours)
            .filter(
                (j): j is number => typeof j === "number" && Number.isFinite(j)
            )
        morceaux.push(
            jours.length > 0
                ? `${enRetard.length} en retard de ${Math.max(...jours)} j`
                : `${enRetard.length} en retard`
        )
    }

    // LES FLUX SANS VERDICT SE COMPTENT AUSSI. Les taire ferait mentir la
    // soustraction : « 4 flux dus · 3 à jour » laisserait croire au quatrième
    // en retard, alors qu'on ne sait simplement rien de lui.
    const sansMesure = servis.filter(
        (f, i) => etats[i] === "attente" && f.derniere_mesure === null
    ).length
    if (sansMesure > 0) morceaux.push(`${sansMesure} sans mesure reçue`)
    const muets = compte("attente") - sansMesure
    if (muets > 0) morceaux.push(`${muets} sans verdict`)

    return morceaux.join(" · ")
}

/**
 * LE VERDICT DU COMPTEUR, lu de ses flux (Q4) : `true` quand ils sont TOUS à
 * jour, `false` dès qu'un seul est en retard, `null` quand le silence des uns
 * empêche de conclure sur l'ensemble. Jamais un état déduit.
 */
export function verdictDesFlux(etats: EtatDeLigne[]): boolean | null {
    if (etats.length === 0) return null
    if (etats.some((e) => e === "retard" || e === "collecte")) return false
    return etats.every((e) => e === "a-jour") ? true : null
}

// ============================================================================
// LE FLUX D'UNE FAMILLE, SOUS SON ONGLET
// ============================================================================

/**
 * QUEL FLUX PORTE CHAQUE FAMILLE. La table est explicite, dans l'ordre de
 * préférence : sous « Puissance », la courbe date la famille quand elle est
 * servie, la PMax prend le relais sur les compteurs qui n'ont que celle-là.
 *
 * Trois familles n'ont VOLONTAIREMENT pas de flux : les dépassements, qui sont
 * CALCULÉS sur la courbe et ne sont pas un flux dû, la facturation, qui est du
 * stock, et l'index, qui est un témoin de communication et jamais un verdict.
 * Toutes trois gardent le mot que le lot 5d4 leur a donné, qui dit déjà
 * exactement ce que le flux dirait.
 */
const FLUX_DE_LA_FAMILLE: Partial<Record<CleOnglet, string[]>> = {
    energie: ["energie"],
    puissance: ["cdc", "pmax"],
}

/**
 * LE FLUX QUI DATE CETTE FAMILLE, DANS CE SENS, ou `null`.
 *
 * `null` = cette famille n'a pas de flux dû servi, et la ligne d'onglet garde
 * exactement les règles d'avant (lots 5h et 5g annexe c). Le sens est EXIGÉ dès
 * qu'il est connu : sur un compteur à deux sens, prendre le flux de l'autre
 * côté ferait dire « à jour » à une injection en retard.
 */
export function fluxDeLaFamille(
    statut: StatusCollectePdl | null | undefined,
    cle: CleOnglet | null | undefined,
    direction?: DirectionEnedis | null
): StatusFluxPdl | null {
    const servis = statut?.flux
    if (!Array.isArray(servis) || !cle) return null
    const attendus = FLUX_DE_LA_FAMILLE[cle]
    if (!attendus) return null
    const dansLeSens = direction
        ? servis.filter((f) => f?.sens === direction)
        : servis
    for (const nom of attendus) {
        const trouve = dansLeSens.find((f) => f?.flux === nom)
        if (trouve) return trouve
    }
    return null
}

/**
 * LA LIGNE D'UNE FAMILLE QUAND SON FLUX EST SERVI : « dernière valeur le
 * 16/09/2026 · attendu J+1 · à jour ».
 *
 * `dateChargee` est la dernière abscisse FINIE réellement chargée (règle du lot
 * 5d4). Quand elle diffère de la date du flux, C'EST ELLE QUI DATE la ligne —
 * c'est la seule qui soit une mesure vue — tandis que le flux garde l'attendu
 * et le verdict, qui sont jugés et non déduits.
 */
export function ligneDeLaFamilleParFlux(params: {
    flux: StatusFluxPdl | null | undefined
    statut: StatusCollectePdl | null | undefined
    /** L'amorce de la phrase (« dernière valeur le »), servie par l'appelant. */
    amorce: string
    /** La date déjà mise en forme, ou `null` si aucune n'est établie. */
    dateChargee: string | null
    aujourdHui?: string
    /**
     * DE QUOI NOMMER LE FLUX (S1 de la review du 23/09). Sous « Puissance », le
     * rappel compte la courbe ET la PMax, et la ligne de fraîcheur ne parlait
     * que de l'une des deux sans jamais dire laquelle.
     */
    contexte?: ContexteDesFlux
    /**
     * LE LIBELLÉ QUE LE RAPPEL PORTE DÉJÀ sur cette même ligne. Le nom du flux
     * n'est écrit que s'il APPREND quelque chose : sous « Énergie », rappel et
     * flux s'appellent tous deux « Énergie journalière », et l'écrire deux fois
     * ferait du bruit là où Mathieu demande de la précision.
     */
    libelleDuRappel?: string | null
}): string | null {
    const flux = params.flux
    if (!flux) return null
    // Le jour civil de PARIS : le jour UTC est la veille entre minuit et 2 h.
    const jour = jourDeLecture()
    const etat = etatDuFlux(flux, params.statut, params.aujourdHui ?? jour)
    const date = params.dateChargee ?? dateFr(flux.derniere_mesure)
    const morceaux: string[] = []
    const nom = libelleDuFlux(flux, params.contexte ?? {})
    if (nom && nom !== params.libelleDuRappel) morceaux.push(nom)
    if (date) morceaux.push(`${params.amorce} ${date}`)
    else if (flux.derniere_mesure === null)
        morceaux.push("aucune mesure reçue à ce jour")
    const attendu = attenduDuFlux(flux)
    if (attendu) morceaux.push(attendu)
    if (etat === "a-jour") morceaux.push("à jour")
    else if (etat === "retard" || etat === "collecte") {
        morceaux.push(
            typeof flux.retard_jours === "number"
                ? `en retard de ${flux.retard_jours} j`
                : "en retard"
        )
    }
    return morceaux.length > 0 ? morceaux.join(" · ") : null
}
